import React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

/**
 * Text - Komponent tekstowy w stylu Radix Themes
 * 
 * Funkcje:
 * - Semantyczne API (as="p"|"span"|"div"|"label" etc.)
 * - Hierarchia rozmiarów (1-9) z 8px scale
 * - Warianty: soft, solid, contrast
 * - Weight, align, transform
 * - Automatyczne wsparcie light/dark mode
 * 
 * @example
 * ```tsx
 * <Text size="5" weight="bold" align="center">
 *   Ważny tekst
 * </Text>
 * 
 * <Text as="label" size="2" color="gray">
 *   Etykieta formularza
 * </Text>
 * ```
 */

const textVariants = cva(
  "ws-text transition-colors",
  {
    variants: {
      // Rozmiary (Radix scale 1-9)
      size: {
        "1": "text-[var(--font-size-1)] leading-[var(--line-height-3)]",
        "2": "text-[var(--font-size-2)] leading-[var(--line-height-3)]",
        "3": "text-[var(--font-size-3)] leading-[var(--line-height-4)]",
        "4": "text-[var(--font-size-4)] leading-[var(--line-height-4)]",
        "5": "text-[var(--font-size-5)] leading-[var(--line-height-4)]",
        "6": "text-[var(--font-size-6)] leading-[var(--line-height-3)]",
        "7": "text-[var(--font-size-7)] leading-[var(--line-height-2)]",
        "8": "text-[var(--font-size-8)] leading-[var(--line-height-2)]",
        "9": "text-[var(--font-size-9)] leading-[var(--line-height-1)]",
      },
      // Weight
      weight: {
        regular: "font-[var(--font-weight-regular)]",
        medium: "font-[var(--font-weight-medium)]",
        semibold: "font-[var(--font-weight-semibold)]",
        bold: "font-[var(--font-weight-bold)]",
        extrabold: "font-[var(--font-weight-extrabold)]",
      },
      // Color variants
      color: {
        default: "text-[var(--text-white-95)]",
        soft: "text-[var(--text-white-80)]",
        muted: "text-[var(--text-white-70)]",
        subtle: "text-[var(--text-white-65)]",
        gray: "text-[var(--gray-11)]",
        pink: "text-[var(--pink-11)]",
        accent: "text-[var(--pink-9)]",
        success: "text-[var(--success-11)]",
        danger: "text-[var(--danger-11)]",
        warning: "text-[var(--warning-11)]",
      },
      // Alignment
      align: {
        left: "text-left",
        center: "text-center",
        right: "text-right",
        justify: "text-justify",
      },
      // Transform
      transform: {
        none: "normal-case",
        uppercase: "uppercase",
        lowercase: "lowercase",
        capitalize: "capitalize",
      },
      // Letter spacing
      tracking: {
        tight: "tracking-[var(--letter-spacing-tight)]",
        normal: "tracking-[var(--letter-spacing-normal)]",
        wide: "tracking-[var(--letter-spacing-wide)]",
      },
      // Truncate
      truncate: {
        true: "truncate",
        false: "",
      },
    },
    defaultVariants: {
      size: "3",
      weight: "regular",
      color: "default",
      align: "left",
      transform: "none",
      tracking: "normal",
      truncate: false,
    },
  }
)

export interface TextProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof textVariants> {
  /** Element HTML do renderowania */
  as?: "p" | "span" | "div" | "label" | "strong" | "em" | "small" | "mark"
  /** Użyj Slot z Radix dla kompozycji */
  asChild?: boolean
}

const Text = React.forwardRef<HTMLElement, TextProps>(
  ({ 
    as: Tag = "span",
    asChild = false,
    className, 
    size, 
    weight, 
    color, 
    align, 
    transform,
    tracking,
    truncate,
    ...props 
  }, ref) => {
    const Comp = asChild ? Slot : Tag
    
    return (
      <Comp
        // @ts-expect-error - Radix Slot type issue with generic elements
        ref={ref}
        className={cn(
          textVariants({ 
            size, 
            weight, 
            color, 
            align, 
            transform, 
            tracking,
            truncate,
          }), 
          className
        )}
        {...props}
      />
    )
  }
)
Text.displayName = "Text"

/**
 * Heading - Skrót dla większych tekstów (H1-H6)
 */
export interface HeadingProps extends TextProps {
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"
  level?: "1" | "2" | "3" | "4" | "5" | "6"
}

const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ as, level, size, weight = "bold", tracking = "tight", ...props }, ref) => {
    // Auto-map level do size jeśli nie podano size
    const autoSize = size || (level ? 
      ({ "1": "9", "2": "8", "3": "7", "4": "6", "5": "5", "6": "4" }[level] as any) 
      : "7"
    )
    
    const Tag = as || (level ? `h${level}` as any : "h2")
    
    return (
      <Text
        as={Tag}
        // @ts-expect-error - ref type mismatch
        ref={ref}
        size={autoSize}
        weight={weight}
        tracking={tracking}
        {...props}
      />
    )
  }
)
Heading.displayName = "Heading"

/**
 * Code - Tekst w stylu kodu
 */
const Code = React.forwardRef<HTMLElement, TextProps>(
  ({ className, ...props }, ref) => (
    <Text
      as="code"
      ref={ref}
      className={cn(
        "font-mono bg-[var(--dark-3)] px-[var(--space-3)] py-[var(--space-2)]",
        "rounded-[var(--radius-sm)] text-[var(--pink-11)]",
        "border border-[var(--dark-a4)]",
        className
      )}
      size="2"
      {...props}
    />
  )
)
Code.displayName = "Code"

export { Text, Heading, Code, textVariants }

